import React, { createContext, useContext, useState, ReactNode } from 'react';

// Types
export interface LineupPlayer {
  player_id: number;
  player_name: string;
  nationality: string;
  avg_bat_pos: number;
  bat_tvaa_per_over: number;
  bowl_tvaa_per_over: number | null;
  role: string;
  lineup_bat_pos: number | string;
}

export interface SubstituteDetails {
  batter_id: number;
  batter_name: string;
  batter_bat_pos: number;
  batter_tvaa: number;
  bowler_id: number;
  bowler_name: string;
  bowler_bat_pos: number;
  bowler_tvaa: number;
}

export interface Lineup {
  lineup_number: number;
  starting_xi: LineupPlayer[];
  substitute_details: SubstituteDetails;
  total_players: number;
  indians: number;
  overseas: number;
}

export interface LineupState {
  lineups: Lineup[];
  isLoading: boolean;
  error: string | null;
  lastGenerated: Date | null;
}

export interface LineupContextType {
  lineupState: LineupState;
  setLineups: (lineups: Lineup[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearLineups: () => void;
  setLastGenerated: (date: Date | null) => void;
}

const LineupContext = createContext<LineupContextType | undefined>(undefined);

export const useLineupContext = () => {
  const context = useContext(LineupContext);
  if (context === undefined) {
    throw new Error('useLineupContext must be used within a LineupProvider');
  }
  return context;
};

interface LineupProviderProps {
  children: ReactNode;
}

export const LineupProvider: React.FC<LineupProviderProps> = ({ children }) => {
  const [lineupState, setLineupState] = useState<LineupState>({
    lineups: [],
    isLoading: false,
    error: null,
    lastGenerated: null,
  });

  const setLineups = (lineups: Lineup[]) => {
    console.log('Setting lineups:', lineups.length);
    setLineupState(prev => ({
      ...prev,
      lineups,
      lastGenerated: new Date(),
    }));
  };

  const setLoading = (loading: boolean) => {
    console.log('Setting loading:', loading);
    setLineupState(prev => ({
      ...prev,
      isLoading: loading,
    }));
  };

  const setError = (error: string | null) => {
    console.log('Setting error:', error);
    setLineupState(prev => ({
      ...prev,
      error,
    }));
  };

  const clearLineups = () => {
    console.log('Clearing lineups');
    setLineupState({
      lineups: [],
      isLoading: false,
      error: null,
      lastGenerated: null,
    });
  };

  const setLastGenerated = (date: Date | null) => {
    setLineupState(prev => ({
      ...prev,
      lastGenerated: date,
    }));
  };

  const value: LineupContextType = {
    lineupState,
    setLineups,
    setLoading,
    setError,
    clearLineups,
    setLastGenerated,
  };

  return (
    <LineupContext.Provider value={value}>
      {children}
    </LineupContext.Provider>
  );
};
